// A team's badge, picked in Edit team (DIRECTION U25, B-440,
// PLAN-2026-10-18-families-and-newcomers §11): its initials, a face its
// manager makes (B-488), an icon the look draws, or an emoji, each shown on
// the team's own colors so choosing is looking rather than imagining, as the
// house colors are.
//
// FOUR TABS: Initials is one choice; Face is the team's face and the door to
// the face maker; Icons are the looks' own drawings; Emoji sit on shelves
// (animals, food, games, nature, faces, things). Every choice is a button
// that says its name to a screen reader and shows it as a tooltip, and the
// chosen one is pressed.

import { useState } from 'react';
import {
  BADGE_EMOJI,
  BADGE_ICONS,
  type EmojiChoice,
  type ChosenBadge,
  type TeamColors,
} from '@fsp/domain';
import { TeamMark } from '../../kit';
import { Button, Segments, Stack, Text } from '../../kit/ui';

type Tab = 'initials' | 'face' | 'icon' | 'emoji';

const SHELVES: EmojiChoice['group'][] = ['Animals', 'Food', 'Games', 'Nature', 'Faces', 'Things'];

export function BadgePicker({
  letters,
  colors,
  value,
  onChange,
  onMakeFace,
}: {
  letters: string;
  colors: TeamColors;
  value: ChosenBadge;
  onChange: (badge: ChosenBadge) => void;
  /** Open the face maker on the team's face, or on *Start from* when it has none. */
  onMakeFace: () => void;
}) {
  const [tab, setTab] = useState<Tab>(value === null ? 'initials' : value.kind);
  const choice = (badge: ChosenBadge, name: string) => {
    const pressed =
      badge === null
        ? value === null
        : badge.kind !== 'face' && value?.kind === badge.kind && value.id === badge.id;
    return (
      <button
        key={
          badge === null ? 'initials' : badge.kind === 'face' ? 'face' : `${badge.kind}:${badge.id}`
        }
        type="button"
        className="fsp-swatch"
        aria-label={name}
        aria-pressed={pressed}
        title={name}
        onClick={() => onChange(badge)}
      >
        <TeamMark team={{ abbreviation: letters, colors, badge }} size={34} />
      </button>
    );
  };
  return (
    <div>
      <div className="fsp-field-label" id="fsp-badge-label">
        Badge
      </div>
      <Stack gap={2}>
        <Segments
          label="Badge kind"
          value={tab}
          onChange={setTab}
          items={[
            { id: 'initials', label: 'Initials' },
            { id: 'face', label: 'Face' },
            { id: 'icon', label: 'Icons' },
            { id: 'emoji', label: 'Emoji' },
          ]}
        />
        {tab === 'initials' ? (
          <div className="fsp-swatches" role="group" aria-labelledby="fsp-badge-label">
            {choice(null, `Initials, ${letters}`)}
          </div>
        ) : tab === 'face' ? (
          // THE TEAM'S FACE, and the way to the maker: build one from hats,
          // hair and glasses, then paint it (B-488).
          <div className="fsp-badge-face">
            {value?.kind === 'face' ? (
              <TeamMark team={{ abbreviation: letters, colors, badge: value }} size={64} />
            ) : (
              <Text tone="secondary" size="sm">
                Build a face from hair, hats, glasses and more, then paint it any colors you like.
              </Text>
            )}
            <Button
              variant={value?.kind === 'face' ? 'secondary' : 'primary'}
              icon="face"
              onClick={onMakeFace}
            >
              {value?.kind === 'face' ? 'Edit face' : 'Make a face'}
            </Button>
          </div>
        ) : tab === 'icon' ? (
          <div className="fsp-swatches" role="group" aria-labelledby="fsp-badge-label">
            {BADGE_ICONS.map((b) => choice({ kind: 'icon', id: b.id }, b.name))}
          </div>
        ) : (
          <Stack gap={2}>
            {SHELVES.map((shelf) => (
              <div key={shelf}>
                <div className="fsp-cap">{shelf}</div>
                <div className="fsp-swatches" role="group" aria-label={shelf}>
                  {BADGE_EMOJI.filter((b) => b.group === shelf).map((b) =>
                    choice({ kind: 'emoji', id: b.id }, b.name),
                  )}
                </div>
              </div>
            ))}
          </Stack>
        )}
      </Stack>
    </div>
  );
}
